import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { aiManagementApi } from '../../api/aiManagement.api';
import { ticketApi } from '../../api/ticket.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Badge, Skeleton, EmptyState, Btn, Modal, Textarea } from '../../components/ui/index';
import { toPersian, timeAgo } from '../../utils/persianNumber';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { Robot } from '@phosphor-icons/react';

const OUTCOME_CONFIG = {
  accepted: { label: 'پذیرفته‌شده (بدون تغییر)', color: '#15803d', bg: '#f0fdf4' },
  edited: { label: 'ویرایش‌شده', color: '#b45309', bg: '#fffbeb' },
  replaced: { label: 'جایگزین‌شده با پاسخ انسانی', color: '#dc2626', bg: '#fef2f2' },
  human_only: { label: 'بدون پیش‌نویس AI', color: '#64748b', bg: '#f1f5f9' },
};

const STATUS_FILTERS = [
  { id: '', label: 'همه' },
  { id: 'accepted', label: 'پذیرفته‌شده' },
  { id: 'edited', label: 'ویرایش‌شده' },
  { id: 'replaced', label: 'رد‌شده' },
];

export default function AIResponseDashboard() {
  const { addToast } = useUI();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [reviewing, setReviewing] = useState(null); // the item being reviewed
  const [chatHistory, setChatHistory] = useState([]);
  const [qualityRating, setQualityRating] = useState('correct');
  const [finalDescription, setFinalDescription] = useState('');
  const [reason, setReason] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [saving, setSaving] = useState(false);
  const reviewTicketRef = useRef(null);

  const load = (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    return aiManagementApi.getAIAnswerReviewList({ status: status || undefined, search: search || undefined, page, page_size: pageSize })
      .then(r => { setItems(r.data?.items || []); setTotal(r.data?.total || 0); })
      .catch(() => { if (showSpinner) addToast('خطا در بارگذاری لیست پاسخ‌ها', 'error'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [status, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(() => load(false), { paused: !!reviewing });

  const openReview = (item) => {
    reviewTicketRef.current = item.ticket_id;
    setReviewing(item);
    setQualityRating('correct');
    setFinalDescription(item.final_answer || item.ai_draft_answer || '');
    setReason('');
    setOverwrite(false);
    setChatHistory([]);
    // Load the customer's full AI answering process for this ticket so the
    // manager can judge the draft against the whole conversation, not just
    // the final AI reply text.
    const ticketId = item.ticket_id;
    ticketApi.getTicketChatHistory(ticketId)
      .then((res) => {
        if (reviewTicketRef.current !== ticketId) return;
        setChatHistory(Array.isArray(res.data?.messages) ? res.data.messages : []);
      })
      .catch(() => {
        if (reviewTicketRef.current === ticketId) setChatHistory([]);
      });
  };

  const submitReview = async () => {
    setSaving(true);
    try {
      await aiManagementApi.reviewAIAnswer({
        ticket_id: reviewing.ticket_id,
        quality_rating: qualityRating,
        final_title: '',
        final_description: finalDescription,
        reason,
        overwrite_response: overwrite ? '1' : '0',
      });
      addToast('بررسی ذخیره و به دیتاست آموزشی اضافه شد', 'success');
      setReviewing(null);
      load();
    } catch { addToast('خطا در ذخیره بررسی', 'error'); }
    setSaving(false);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="fade-in">
      <PageHeader title="بررسی پاسخ‌های AI" subtitle="مقایسه سوال، پاسخ AI و پاسخ نهایی ارسال‌شده"
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/ai/manager"><Btn variant="secondary">داشبورد عملکرد</Btn></Link>
            <span style={{ background: '#7c3aed', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, alignSelf: 'center', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Robot size={13} weight="regular" /> AI Center</span>
          </div>
        }
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {STATUS_FILTERS.map(f => (
          <button key={f.id} onClick={() => { setStatus(f.id); setPage(1); }}
            style={{ padding: '7px 16px', borderRadius: 20, border: `1.5px solid ${status === f.id ? '#7c3aed' : '#e2e8f0'}`, background: status === f.id ? '#f3e8ff' : '#fff', color: status === f.id ? '#6d28d9' : '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {f.label}
          </button>
        ))}
        <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && (setPage(1), load())}
          placeholder="جستجو در عنوان/متن تیکت..." style={{ marginRight: 'auto', padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 12, outline: 'none', fontFamily: 'inherit', minWidth: 200 }} />
        <Btn size="sm" variant="secondary" onClick={() => { setPage(1); load(); }}>جستجو</Btn>
      </div>

      {loading ? <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{[1, 2, 3].map(k => <div key={k} style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e2e8f0' }}><Skeleton h={18} w="40%" style={{ marginBottom: 10 }} /><Skeleton h={14} /></div>)}</div>
        : items.length === 0 ? <EmptyState icon={<Robot size={44} weight="regular" />} title="موردی یافت نشد" />
        : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {items.map(r => {
              const conf = OUTCOME_CONFIG[r.outcome] || OUTCOME_CONFIG.human_only;
              return (
                <div key={r.ticket_id} style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(139,92,246,0.12)', padding: 20, boxShadow: '0 2px 8px rgba(139,92,246,0.06)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', background: '#f3e8ff', padding: '2px 8px', borderRadius: 10, display: 'inline-flex', alignItems: 'center', gap: 3 }}><Robot size={12} weight="regular" /> #{toPersian(r.ticket_id)}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{r.ticket_title}</span>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>{r.department_name}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <span style={{ fontSize: 11, color: conf.color, background: conf.bg, padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{conf.label}</span>
                      {r.satisfaction && <Badge variant={r.satisfaction === 'satisfied' ? 'green' : 'red'}>{r.satisfaction === 'satisfied' ? 'راضی' : 'ناراضی'}</Badge>}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', marginBottom: 4 }}>پاسخ AI</div>
                      <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, margin: 0 }}>{r.ai_draft_answer || '—'}</p>
                      {r.confidence_score != null && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6 }}>اطمینان: {toPersian(r.confidence_score)}%</div>}
                    </div>
                    <div style={{ background: 'rgba(139,92,246,0.04)', border: '1px solid rgba(139,92,246,0.1)', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', marginBottom: 4 }}>پاسخ نهایی ارسال‌شده</div>
                      <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, margin: 0 }}>{r.final_answer || '—'}</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{timeAgo(r.send_date)}</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Link to={`/tickets/${r.ticket_id}/process`} style={{ textDecoration: 'none' }}>
                        <Btn size="sm" variant="secondary">مشاهده تیکت</Btn>
                      </Link>
                      <Btn size="sm" onClick={() => openReview(r)}>بررسی و بهبود پاسخ</Btn>
                    </div>
                  </div>
                </div>
              );
            })}

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
                <Btn size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>قبلی</Btn>
                <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'center' }}>{toPersian(page)} از {toPersian(totalPages)}</span>
                <Btn size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>بعدی</Btn>
              </div>
            )}
          </div>
        )
      }

      <Modal open={!!reviewing} onClose={() => setReviewing(null)} title="بررسی و بهبود پاسخ AI" width={720}>
        {reviewing && (
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6, display: 'block' }}>سوال کاربر</label>
              <p style={{ fontSize: 13, color: '#475569', background: '#f8fafc', borderRadius: 8, padding: 10, margin: 0 }}>{reviewing.user_question}</p>
            </div>
            {chatHistory.length > 0 && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#6d28d9', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}><Robot size={14} weight="regular" /> تاریخچه گفتگو با هوش‌مصنوعی</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 220, overflowY: 'auto', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8, padding: 10 }}>
                  {chatHistory.map((m, i) => (
                    <div key={i} style={{
                      alignSelf: m.role === 'user' ? 'flex-start' : 'flex-end',
                      maxWidth: '90%', padding: '6px 10px', borderRadius: 8, fontSize: 12, whiteSpace: 'pre-wrap',
                      background: m.role === 'user' ? '#EDF3FC' : '#fff',
                      border: m.role === 'user' ? '1px solid #B7C9EC' : '1px solid #e9d5ff',
                      color: '#334155',
                    }}>
                      <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 2, color: m.role === 'user' ? '#29AAE1' : '#7c3aed' }}>
                        {m.role === 'user' ? 'مشتری' : 'AI'}
                      </div>
                      {m.content}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6, display: 'block' }}>وضعیت کیفیت</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['correct', 'صحیح', '#059669'], ['needs_improvement', 'نیاز به بهبود', '#f59e0b'], ['wrong', 'اشتباه', '#dc2626']].map(([id, label, color]) => (
                  <button key={id} onClick={() => setQualityRating(id)} style={{ padding: '7px 14px', borderRadius: 8, border: `1.5px solid ${qualityRating === id ? color : '#e2e8f0'}`, background: qualityRating === id ? color + '15' : '#fff', color: qualityRating === id ? color : '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>{label}</button>
                ))}
              </div>
            </div>
            <Textarea label="متن پاسخ بهبودیافته" value={finalDescription} onChange={e => setFinalDescription(e.target.value)} rows={4} />
            <Textarea label="دلیل تغییر (برای دیتاست آموزشی)" value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="مثلاً: پاسخ AI ناقص بود، اطلاعات فنی نادرست داشت..." />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#334155' }}>
              <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} />
              پاسخ ارسال‌شده به مشتری هم با متن بهبودیافته جایگزین شود
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="secondary" onClick={() => setReviewing(null)}>انصراف</Btn>
              <Btn onClick={submitReview} loading={saving}>ذخیره بررسی</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
